import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Upload,
  FileText,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
  Package,
  Boxes,
} from 'lucide-react';
import { aiAssetsAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Card, CardContent } from '../../../components/ui/Card';
import { FormLabel } from '../../../components/ui/FormLabel';
import { useToast } from '../../../hooks/useToast';
import { ASSET_CATEGORIES } from '../../../lib/assetConstants';
import { STOCK_UNIT_TYPES, STOCK_CATEGORIES } from '../../../lib/stockConstants';

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500';
const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const validateFile = (file) => {
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
    return { valid: false, error: 'Only PDF, JPEG, PNG, and WebP files are allowed' };
  }
  if (file.size > 10 * 1024 * 1024) {
    return { valid: false, error: 'File must be less than 10 MB' };
  }
  return { valid: true };
};

const defaultReviewFields = (extracted = {}, recordType = 'asset') => ({
  recordType,
  name: extracted.name || '',
  category: extracted.category || '',
  manufacturer: extracted.manufacturer || '',
  model: extracted.model || '',
  serialNumber: extracted.serial_number || '',
  purchaseDate: extracted.purchase_date || '',
  supplierName: extracted.supplier_name || '',
  purchaseReceiptRef: extracted.purchase_receipt_ref || '',
  purchaseGrossAmount: extracted.purchase_gross_amount ?? '',
  sku: extracted.sku || '',
  quantity: extracted.quantity ?? 1,
  unitType: extracted.unit_type || 'each',
  storageLocation: extracted.storage_location || '',
});

const AssetImport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get('draft');
  const { success, error, ToastContainer } = useToast();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [reviewFields, setReviewFields] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (draftIdParam) {
      loadDraft(draftIdParam);
    }
  }, [draftIdParam]);

  const loadDraft = async (id) => {
    try {
      const res = await aiAssetsAPI.getDraft(id);
      const d = res.draft;
      setDraft(d);
      const extracted =
        typeof d.extractedData === 'string' ? JSON.parse(d.extractedData) : d.extractedData || {};
      setReviewFields(
        defaultReviewFields(extracted, d.suggestedRecordType || extracted.suggested_record_type || 'asset')
      );
    } catch (err) {
      error(err.message || 'Failed to load draft');
    }
  };

  const handleFileSelect = (selected) => {
    const f = selected?.[0];
    if (!f) return;
    const validation = validateFile(f);
    if (!validation.valid) {
      setErrorMessage(validation.error);
      return;
    }
    setFile(f);
    setErrorMessage('');
    setDraft(null);
    setReviewFields(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setErrorMessage('Please select a file');
      return;
    }
    setUploading(true);
    setErrorMessage('');
    try {
      const result = await aiAssetsAPI.upload(file);
      const d = result.draft;
      setDraft(d);
      const extracted =
        typeof d.extractedData === 'string'
          ? JSON.parse(d.extractedData)
          : d.extracted_data
            ? typeof d.extracted_data === 'string'
              ? JSON.parse(d.extracted_data)
              : d.extracted_data
            : d.extractedData || {};
      const recordType =
        result.extraction?.suggestedRecordType ||
        d.suggestedRecordType ||
        extracted.suggested_record_type ||
        'asset';
      setReviewFields(defaultReviewFields(extracted, recordType));
      if (result.message) success(result.message);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      setErrorMessage(msg);
      error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleAccept = async () => {
    if (!draft?.id || !reviewFields?.name?.trim()) {
      error('Name is required');
      return;
    }
    setAccepting(true);
    try {
      const payload = {
        recordType: reviewFields.recordType,
        name: reviewFields.name.trim(),
        category: reviewFields.category || null,
        purchaseDate: reviewFields.purchaseDate || null,
        sku: reviewFields.sku || null,
        quantity: reviewFields.quantity,
        unitType: reviewFields.unitType,
      };
      const result = await aiAssetsAPI.accept(draft.id, payload);
      success(result.message || 'Record created successfully');
      if (result.recordType === 'stock' && result.stockItem?.id) {
        navigate(`/tenant/stock/${result.stockItem.id}`);
      } else if (result.asset?.id) {
        navigate(`/tenant/assets/${result.asset.id}`);
      } else {
        navigate('/tenant/assets');
      }
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Accept failed');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!draft?.id) return;
    if (!confirm('Reject this draft?')) return;
    try {
      await aiAssetsAPI.reject(draft.id);
      success('Draft rejected');
      setDraft(null);
      setReviewFields(null);
      setFile(null);
    } catch (err) {
      error(err.message || 'Reject failed');
    }
  };

  const updateField = (key, value) => {
    setReviewFields((prev) => ({ ...prev, [key]: value }));
  };

  const isProcessing = draft?.status === 'processing';
  const canReview =
    draft &&
    ['completed', 'needs_review', 'failed'].includes(draft.status) &&
    reviewFields;

  return (
    <Layout>
      <ToastContainer />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-h1 font-medium text-[var(--ds-text-primary)] mb-2">
            Import asset or stock
          </h1>
          <p className="text-body text-[var(--ds-text-secondary)]">
            Upload a purchase receipt (PDF or image) for AI extraction, then review and confirm
          </p>
        </div>

        {!draft && (
          <Card className="mb-6 bg-white dark:bg-gray-800">
            <CardContent className="p-6">
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFileSelect(e.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
                <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-700 dark:text-gray-300">
                  Drag and drop or click to select a receipt
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  PDF, JPEG, PNG, WebP — max 10 MB
                </p>
              </div>

              {file && (
                <div className="mt-4 flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <span className="text-sm text-gray-900 dark:text-gray-100">{file.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {errorMessage && (
                <div className="mt-4 flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {errorMessage}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <Button variant="primary" onClick={handleUpload} disabled={!file || uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Upload & extract'
                  )}
                </Button>
                <Button variant="outline" onClick={() => navigate('/tenant/assets')}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isProcessing && (
          <Card className="bg-white dark:bg-gray-800">
            <CardContent className="p-6 flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="text-gray-700 dark:text-gray-300">AI is processing your receipt...</span>
            </CardContent>
          </Card>
        )}

        {canReview && (
          <Card className="bg-white dark:bg-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                {draft.status === 'failed' ? (
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                )}
                <h2 className="text-h2 font-medium text-gray-900 dark:text-gray-100">
                  Review extracted data
                </h2>
              </div>

              {draft.status === 'failed' && draft.errorMessage && (
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                  AI extraction failed: {draft.errorMessage}. You can still enter details manually.
                </p>
              )}

              <div className="mb-4 flex gap-2">
                <Button
                  variant={reviewFields.recordType === 'asset' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => updateField('recordType', 'asset')}
                >
                  <Package className="w-4 h-4 mr-1" />
                  Asset
                </Button>
                <Button
                  variant={reviewFields.recordType === 'stock' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => updateField('recordType', 'stock')}
                >
                  <Boxes className="w-4 h-4 mr-1" />
                  Stock
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormLabel className="text-gray-700 dark:text-gray-300">Name *</FormLabel>
                  <Input
                    value={reviewFields.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    className={inputClass}
                  />
                </div>

                {reviewFields.recordType === 'asset' ? (
                  <>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Category</FormLabel>
                      <select
                        value={reviewFields.category}
                        onChange={(e) => updateField('category', e.target.value)}
                        className={selectClass}
                      >
                        <option value="">—</option>
                        {ASSET_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Serial number</FormLabel>
                      <Input
                        value={reviewFields.serialNumber}
                        onChange={(e) => updateField('serialNumber', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Manufacturer</FormLabel>
                      <Input
                        value={reviewFields.manufacturer}
                        onChange={(e) => updateField('manufacturer', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Model</FormLabel>
                      <Input
                        value={reviewFields.model}
                        onChange={(e) => updateField('model', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">SKU</FormLabel>
                      <Input
                        value={reviewFields.sku}
                        onChange={(e) => updateField('sku', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Category</FormLabel>
                      <select
                        value={reviewFields.category}
                        onChange={(e) => updateField('category', e.target.value)}
                        className={selectClass}
                      >
                        <option value="">—</option>
                        {STOCK_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Quantity</FormLabel>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        value={reviewFields.quantity}
                        onChange={(e) => updateField('quantity', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Unit</FormLabel>
                      <select
                        value={reviewFields.unitType}
                        onChange={(e) => updateField('unitType', e.target.value)}
                        className={selectClass}
                      >
                        {STOCK_UNIT_TYPES.map((u) => (
                          <option key={u.value} value={u.value}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Purchase date</FormLabel>
                  <Input
                    type="date"
                    value={reviewFields.purchaseDate}
                    onChange={(e) => updateField('purchaseDate', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Supplier</FormLabel>
                  <Input
                    value={reviewFields.supplierName}
                    onChange={(e) => updateField('supplierName', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Receipt / invoice ref</FormLabel>
                  <Input
                    value={reviewFields.purchaseReceiptRef}
                    onChange={(e) => updateField('purchaseReceiptRef', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Gross amount (£)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={reviewFields.purchaseGrossAmount}
                    onChange={(e) => updateField('purchaseGrossAmount', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button variant="primary" onClick={handleAccept} disabled={accepting}>
                  {accepting ? 'Creating...' : 'Accept & create record'}
                </Button>
                <Button variant="outline" onClick={handleReject}>
                  Reject draft
                </Button>
                <Button variant="ghost" onClick={() => navigate('/tenant/assets')}>
                  Back to assets
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AssetImport;
